import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PrenatalIntakeFormData } from "../PrenatalIntakeFormSchema";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Heart } from "lucide-react";

interface SocialEnvironmentSectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function SocialEnvironmentSection({ form }: SocialEnvironmentSectionProps) {
  const hasSupportSystem = form.watch("has_support_system");

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Environmental and Social Factors</h3>

      <Alert variant="default" className="bg-muted">
        <Heart className="h-4 w-4" />
        <AlertDescription>
          These questions help us connect you with resources and support. Your answers are confidential.
        </AlertDescription>
      </Alert>

      {/* Housing */}
      <div className="space-y-4">
        <h4 className="font-medium">Housing & Safety</h4>
        
        <FormField
          control={form.control}
          name="housing_concerns"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Had housing concerns in the past three months</FormLabel>
                <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="feels_safe_at_home"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I feel safe where I live</FormLabel>
                <FormDescription>Unchecked is a risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>

      {/* Food & Transportation */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Food & Transportation</h4>
        
        <FormField
          control={form.control}
          name="food_insecurity"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Skipped meals, not eaten when hungry, or used a food pantry</FormLabel>
                <FormDescription>In the past month due to not having enough money for food. * Risk factor</FormDescription>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="transportation_barriers"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Problems getting to health care or social services appointments</FormLabel>
                <FormDescription>Transportation or childcare barriers. * Risk factor</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>

      {/* Abuse History */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Relationship Safety</h4>
        
        <FormField
          control={form.control}
          name="history_of_abuse"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Ever been physically, sexually, emotionally, or verbally abused</FormLabel>
                <FormDescription>By current partner, ex-partner, or anyone close. * Risk factor</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>

      {/* Support System */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Support System</h4>
        
        <FormField
          control={form.control}
          name="has_support_system"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I have people in my life I can count on when I need help</FormLabel>
                <FormDescription>Unchecked is a risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />

        {hasSupportSystem && (
          <FormField
            control={form.control}
            name="support_system_details"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>Who can you count on for help with everyday activities?</FormLabel>
                <FormDescription>Childcare, cooking, laundry, transportation, etc.</FormDescription>
                <FormControl>
                  <Textarea 
                    placeholder="e.g., My mom, my sister, my partner..." 
                    className="min-h-[80px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {/* Language */}
      <div className="pt-4 border-t">
        <FormField
          control={form.control}
          name="is_english_fluent"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>I am fluent in and comfortable with English</FormLabel>
                <FormDescription>Unchecked is a risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
